import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import TrackRow from "@/components/music/TrackRow";
import { toast } from "sonner";
import { Globe, ListMusic, Loader2, Lock, Play, Trash2 } from "lucide-react";

export default function PlaylistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const { playQueue } = useMusicPlayer();
  const { t } = useI18n();

  const load = () => api.get(`/music/playlists/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  // `load` is rebuilt every render; the playlist is refetched when its id
  // changes, not whenever this component happens to re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const removeFromPlaylist = async (track) => {
    await api.delete(`/music/playlists/${id}/tracks/${track.id}`);
    toast.success(t("removed_from_playlist"));
    window.dispatchEvent(new Event("playlists-changed"));
    load();
  };

  // Private unless the owner says otherwise: a playlist is somebody's own
  // selection, and sharing it should be a decision rather than a default.
  const toggleShared = async () => {
    try {
      const { data: saved } = await api.patch(`/music/playlists/${id}`, { is_public: !data.is_public });
      setData((d) => ({ ...d, is_public: saved.is_public }));
      toast.success(saved.is_public ? t("playlist_shared") : t("playlist_private"));
    } catch (e) {
      toast.error(t("could_not_add"));
    }
  };

  const deletePlaylist = async () => {
    if (!window.confirm(t("delete_playlist_confirm"))) return;
    await api.delete(`/music/playlists/${id}`);
    toast.success(t("playlist_deleted"));
    window.dispatchEvent(new Event("playlists-changed"));
    navigate("/app/library");
  };

  if (data === null) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (data === false) return <div className="p-10 text-zinc-400">{t("playlist_not_found")}</div>;

  return (
    <div>
      <div className="p-4 md:p-8 pb-6 bg-gradient-to-b from-[#FF2A4D]/25 to-transparent flex items-end gap-6">
        <div className="w-40 h-40 rounded-lg bg-gradient-to-br from-[#FF2A4D]/60 to-purple-600/40 flex items-center justify-center shadow-2xl shrink-0">
          <ListMusic className="w-16 h-16 text-white" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-bold">{t("playlist")}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-2">{data.name}</h1>
          <p className="text-zinc-300 mt-3 text-sm">
            {data.tracks.length} {t("tracks")}
            {data.is_owner === false && data.owner_name ? ` · ${data.owner_name}` : ""}
          </p>
        </div>
      </div>

      <div className="px-8 py-4 flex items-center gap-4">
        <button data-testid="playlist-play-all" onClick={() => data.tracks.length && playQueue(data.tracks, 0)} className="w-14 h-14 rounded-full bg-[#FF2A4D] hover:bg-[#E61C3D] hover:scale-105 flex items-center justify-center transition-all shadow-[0_0_20px_rgba(255,69,0,0.4)]">
          <Play className="w-6 h-6 text-white ml-1" fill="white" />
        </button>
        {data.is_owner !== false && (
          <>
            <button
              onClick={toggleShared}
              data-testid="playlist-share-toggle"
              title={data.is_public ? t("playlist_public") : t("playlist_private_state")}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors ${data.is_public ? "bg-white/15 text-white" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}
            >
              {data.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {data.is_public ? t("playlist_public") : t("playlist_private_state")}
            </button>
            <button onClick={deletePlaylist} data-testid="delete-playlist" className="text-zinc-400 hover:text-red-400 p-2 rounded-full hover:bg-white/10">
              <Trash2 className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      <div className="px-5 pb-6">
        {data.tracks.length === 0 ? (
          <p className="text-zinc-500 text-sm px-3 py-8 text-center">{t("playlist_empty")}</p>
        ) : (
          data.tracks.map((tr, i) => (
            <TrackRow key={tr.id} track={tr} index={i} tracks={data.tracks} onRemove={removeFromPlaylist} showAdd={false} />
          ))
        )}
      </div>
    </div>
  );
}
